import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Share2,
  Flag,
  Eye,
  ThumbsUp,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from '@/app/hooks/use-toast';

import { useForum } from '../useForum';
import { VoteButton } from './VoteButton';
import { MarkdownContent } from './MarkdownContent';
import { CommentSection } from './CommentTree';
import { ThreadDetailSkeleton } from './ThreadDetailSkeleton';
import {
  displaySpaceName,
  excerptOf,
  initialsOf,
  isHotThread,
  isUnanswered,
  threadTags,
} from '../forum-display';

/**
 * One thread, read.
 *
 * Rebuilt to the public article variant of the admin app's forum redesign
 * (ai2aim-ems 3bb83bc). That design ships two variants of this screen and
 * describes the public one as the marketing reading experience rather than a
 * workspace one: no enclosing card, an uppercase back link, a display-scale
 * heading, and a reading time. This site is only ever the public one, so that
 * is the only variant here.
 *
 * WHAT CAME OUT:
 *
 *   "Community Member" under every byline. Nobody had earned it and nothing
 *   asserted it -- it was printed under every author regardless. A label the
 *   page invents about a person is the same fabrication as an invented quote.
 *
 *   The generic user glyph in the avatar, which identified nothing. The
 *   author's initials identify the author, and match the rows on the space
 *   page and the index.
 *
 *   `post.views ?? 0`, which turned a missing measurement into a measured
 *   zero. The stat is omitted when the endpoint does not project one.
 *
 * Hot now reads the shared rule rather than a second one written here: it was
 * `upvotes - downvotes >= 20` in this file and `upvotes >= 20` everywhere
 * else, so a thread could be hot in a list and cold on its own page.
 */

/** Words over 220, floored at one. Computed from the body, never invented. */
function readingMinutes(content: string): number {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

const MUTED = 'rgba(var(--ink-rgb),.62)';
const LINE = 'rgba(var(--ink-rgb),.13)';

export function ThreadDetail() {
  const { postId } = useParams<{ postId: string }>();
  const {
    getPostDetail,
    getUserVote,
    voteOnPost,
    getSubforum,
    fetchPost,
    fetchComments,
    recordPostView,
    state,
  } = useForum();

  useEffect(() => {
    if (postId) {
      fetchPost(postId);
      fetchComments(postId);
    }
  }, [postId, fetchPost, fetchComments]);

  useEffect(() => {
    if (postId) recordPostView(postId);
  }, [postId, recordPostView]);

  if (!postId) {
    return (
      <div className="container py-20 text-center">
        <p className="text-lg" style={{ color: MUTED }}>
          Post not found
        </p>
      </div>
    );
  }

  // The post detail route's copy, never a list row: list rows are losing the
  // body (T100526003). A re-read after a vote or a reply keeps showing the
  // loaded thread; the skeleton is only for the first read.
  const post = getPostDetail(postId);
  const isLoadingPost = state.loading.post.has(postId);
  const postError = state.errors.post.get(postId);

  if (!post && (isLoadingPost || !postError)) {
    return <ThreadDetailSkeleton />;
  }

  if (!post) {
    return (
      <div className="container py-20">
        <div className="mx-auto max-w-md space-y-4 text-center">
          <div
            className="mx-auto grid h-16 w-16 place-items-center rounded-2xl"
            style={{ background: 'rgba(var(--ink-rgb),.06)' }}
          >
            <MessageSquare size={30} style={{ color: 'rgba(var(--ink-rgb),.3)' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>
            Post not found
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: MUTED }}>
            This post does not exist or may have been removed.
          </p>
          <Button asChild>
            <Link to="/forum">Back to Forum</Link>
          </Button>
        </div>
      </div>
    );
  }

  const subforum = getSubforum(post.subforumId);
  const subforumUrl = subforum ? `/forum/${subforum.slug}` : '/forum';
  const userVote = getUserVote(post.id, 'post');

  // The trailing hashtag block is rendered as its own chips below, so it is
  // stripped from the prose rather than repeated at the foot of the article.
  const body = post.content.replace(/\n+\s*(#[\w-]+\s*)+$/, '').trim();
  const tags = threadTags(post);

  // A standfirst only when there is an article to lead into. On a one-line
  // post the excerpt IS the body, and printing both says the same sentence
  // twice.
  const lede = body.length > 220 ? excerptOf(post.content) : '';

  const badge = isHotThread(post) ? 'Hot' : isUnanswered(post) ? 'Unanswered' : null;

  const postedOn = new Date(post.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const handleReport = () => {
    const url = window.location.href;
    const subject = encodeURIComponent(`Forum report: ${post.title}`);
    const mailBody = encodeURIComponent(
      `I would like to report the following post:\n\nPost: ${post.title}\nLink: ${url}\n\nReason:\n`,
    );
    window.location.href = `mailto:contact@ai2aim.ai?subject=${subject}&body=${mailBody}`;
    toast({
      title: 'Report',
      description: 'Your email client will open to submit the report.',
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url, title: post.title, text: post.title });
        return;
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Post link copied to clipboard.' });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Could not copy link to clipboard.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="px-6 pb-20 pt-24">
      <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-5">
        <Link
          to={subforumUrl}
          className="dc-h-cyan inline-flex items-center gap-2 self-start text-[11.5px] font-semibold uppercase tracking-[0.1em]"
          style={{ color: MUTED }}
        >
          <ArrowLeft size={14} aria-hidden="true" />
          {subforum ? `Back to ${displaySpaceName(subforum.name)}` : 'Back to forum'}
        </Link>

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={subforumUrl}
              className="rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold"
              style={{
                background: 'rgba(var(--acc2-rgb),.12)',
                color: 'var(--acc2)',
              }}
            >
              {subforum ? displaySpaceName(subforum.name) : 'Forum'}
            </Link>
            {badge && (
              <span
                className="rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold"
                style={
                  badge === 'Hot'
                    ? { background: 'rgba(var(--acc2-rgb),.12)', color: 'var(--acc2)' }
                    : { background: 'rgba(var(--ink-rgb),.06)', color: MUTED }
                }
              >
                {badge}
              </span>
            )}
            <span className="text-xs font-medium" style={{ color: MUTED }}>
              {postedOn} &middot; {readingMinutes(body)} min read
            </span>
          </div>

          <h1
            className="max-w-[18ch] text-[clamp(2.125rem,4.4vw,3.25rem)] font-extrabold leading-[1.04] tracking-[-0.026em]"
            style={{ color: 'var(--ink)', textWrap: 'pretty' }}
          >
            {post.title}
          </h1>

          {lede && (
            <p
              className="max-w-[60ch] text-[19px] leading-[1.6]"
              style={{ color: MUTED, textWrap: 'pretty' }}
            >
              {lede}
            </p>
          )}

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md px-2 py-0.5 text-[11px] font-medium"
                  style={{ background: 'rgba(var(--ink-rgb),.06)', color: MUTED }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div
            className="flex flex-wrap items-center justify-between gap-4 py-[18px]"
            style={{ borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}
          >
            <div className="flex items-center gap-3">
              <span
                className="grid size-[42px] shrink-0 place-items-center rounded-full text-sm font-bold"
                style={{ background: 'var(--acc2)', color: 'var(--btnfg)' }}
              >
                {initialsOf(post.author)}
              </span>
              <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                {post.author}
              </span>
            </div>

            <div
              className="flex flex-wrap items-center gap-5 text-[12.5px] font-medium"
              style={{ color: MUTED }}
            >
              {typeof post.views === 'number' && (
                <span className="inline-flex items-center gap-1.5">
                  <Eye size={13} /> {post.views} views
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <ThumbsUp size={13} /> {post.upvotes} upvotes
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MessageSquare size={13} /> {post.commentCount}{' '}
                {post.commentCount === 1 ? 'reply' : 'replies'}
              </span>
              <VoteButton
                upvotes={post.upvotes}
                downvotes={post.downvotes}
                userVote={userVote}
                onVote={(voteType) =>
                  voteOnPost(post.id, voteType).catch(console.error)
                }
                compact
              />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <div
            className="prose prose-slate dark:prose-invert max-w-[68ch] text-[16.5px] leading-[1.75]"
            style={{ color: 'rgba(var(--ink-rgb),.86)' }}
          >
            <MarkdownContent content={body} />
          </div>
        </div>

        <div
          className="flex flex-wrap items-center gap-6 pb-8 pt-6"
          style={{ borderBottom: `1px solid ${LINE}` }}
        >
          <a
            href="#comments"
            className="dc-h-cyan inline-flex items-center gap-2 text-[13px] font-semibold"
            style={{ color: MUTED }}
          >
            <MessageSquare size={16} /> {post.commentCount}{' '}
            {post.commentCount === 1 ? 'reply' : 'replies'}
          </a>
          <button
            type="button"
            onClick={handleShare}
            className="dc-h-cyan inline-flex items-center gap-2 text-[13px] font-semibold"
            style={{ color: MUTED }}
          >
            <Share2 size={16} /> Share
          </button>
          <button
            type="button"
            onClick={handleReport}
            className="dc-h-cyan inline-flex items-center gap-2 text-[12.5px] font-semibold"
            style={{ color: MUTED }}
          >
            <Flag size={16} /> Report
          </button>
        </div>

        <div id="comments">
          <CommentSection postId={post.id} />
        </div>
      </div>
    </div>
  );
}
